import {OnBoardingTooltipModel} from "../on-boarding-tooltip.model";
import {IOnBoardingTooltipsContainer} from "../on-boarding-tooltips-container.interface";
import {IServiceFactoryExtended} from "../../../service-factory-extended.interface";
import {IconTitleTextTooltip} from "../../../../components/tooltip/tooltips/icon-title-text/icon-title-text.tooltip";
import {CyClass} from "../../../../../common/cy-classes";
import {TimeSpan} from "@sparkware/app-utils";
import {makeObservable, observable, reaction, runInAction} from "mobx";
import {TOOLTIP_TARGET_BOTTOM_NAV_BAR_INDICATOR} from "../on-boarding-tooltip-target-selectors";
import {DomEventSubscription} from "../../../../../utils/event-listener-registration";
import {MenuItemKey} from "../../../configurations/config-data-sections/generated-config-const";
import {stickOnTopTooltipModifier} from "../../tooltip-base/tooltip-modifiers";

export class VideoSlidesExplorerOnBoardingTooltipModel extends OnBoardingTooltipModel {
    private _onScrollSubscription: DomEventSubscription | null = null;

    constructor(tooltipsContainer: IOnBoardingTooltipsContainer,
        services: IServiceFactoryExtended) {

        super(tooltipsContainer, services, {
            renderContent: () => {
                const Icon = services.theme.currentTheme.icons.exploreOutlinedIcon;
                return (<IconTitleTextTooltip icon={<Icon />}
                    title={services.translation.translate("ONBOARDING_BOTTOM_NAV_BAR_EXPLORE_MAIN_TEXT")} 
                    text={services.translation.translate("ONBOARDING_BOTTOM_NAV_BAR_EXPLORE_SECONDARY_TEXT")} 
                    onClick={this._onTooltipClicked}
                    className={CyClass('cy-video-slides-onboarding-explorer')} />);
            },
            placement: "top",
            modifiers: [stickOnTopTooltipModifier],
            timeout: TimeSpan.fromSeconds(7),
            localStorageKey: 'video_slides_onboarding_explorer_tooltip',
            maximumDisplayCount: 1,        
            hasGlow: true,
            showOverlay: true,
            showCloseButton: false,
            closeOnClickOutside: true,
            animated: true,
        });

        makeObservable(this);

        reaction(() => this.services.user.isAuthorized, (isAuthorized) => {
            if (isAuthorized && !this._onScrollSubscription) {
                // set a scroll event listener because the onboarding should be shown after user start's scrolling, once authenticated
                this._onScrollSubscription = this.services.window.subscribeTo.scroll(this._onWindowScroll);
            }
        }, {
            fireImmediately: true
        });


    }

    @observable.ref
    scrollDetected: boolean = false;

    private _onWindowScroll = () => {
        // Check if this.services.routing.currentRoute?.data?.arenaType because we show onboarding only for home page and arenas
        if (this.services.routing.currentRoute?.isOrbitRoute && this.services.window.scrollY > 200) {
            runInAction(() => {
                this.scrollDetected = true;
            });
        }
    };

    private _removeScrollSubscription = () => {
        // remove scroll event listener
        if (this._onScrollSubscription) {
            this._onScrollSubscription.dispose();
            this._onScrollSubscription = null;
        }
    };

    shouldDisplay(): boolean {
        const commonConditions = super.shouldDisplay();
        const videoSlidesShouldBeDisaplayed = this.services.videoSlides.shouldBeDisplayed;

        return commonConditions && videoSlidesShouldBeDisaplayed && this.scrollDetected;
    }

    protected _getElementToAttach(): HTMLElement | null {
        const EXPLORE_MENU_ITEM: MenuItemKey = 'videoSlideExploreMenuItem';
        return this.services.dom.querySelector(`.${TOOLTIP_TARGET_BOTTOM_NAV_BAR_INDICATOR}.${EXPLORE_MENU_ITEM}`) as HTMLElement; 
    }

    onToolTipRendered(tooltipContainer: HTMLElement): void {
        super.onToolTipRendered(tooltipContainer);

        this._removeScrollSubscription();

        this.services.analytics.sendOrbitUserFlowEvent("Tooltip", "Show", "TT_Bubble");
    }

    private _onTooltipClicked = async () => {
        this.closeTooltip();

        this._getElementToAttach().click();

        this.services.analytics.sendOrbitUserFlowEvent("Tooltip", "Click", "TT_Bubble");      
    }
}